import { useState } from 'react';
import type { Recomendacion } from '../../../hooks/useClienteRecomendaciones';

interface CombinacionesProps {
  recomendaciones: Recomendacion[];
  onMarkAsRead: (id: number) => void;
  loading: boolean;
}

const tipoConfig: Record<string, { label: string; icon: string; bg: string; text: string }> = {
  combinacion: {
    label: 'Combinación',
    icon: 'ri-magic-line',
    bg: 'bg-brand-sage/20',
    text: 'text-brand-black',
  },
  alerta: {
    label: 'Alerta',
    icon: 'ri-error-warning-line',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
  },
  sugerencia: {
    label: 'Sugerencia',
    icon: 'ri-lightbulb-line',
    bg: 'bg-sky-50',
    text: 'text-sky-700',
  },
};

export default function Combinaciones({ recomendaciones, onMarkAsRead, loading }: CombinacionesProps) {
  const [filter, setFilter] = useState<'todas' | 'nuevas' | 'leidas'>('todas');

  const filtered = recomendaciones.filter((r) => {
    if (filter === 'nuevas') return !r.leida;
    if (filter === 'leidas') return r.leida;
    return true;
  });

  const nuevasCount = recomendaciones.filter((r) => !r.leida).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-brand-black text-3xl font-light">
            Recomendaciones
          </h1>
          <p className="font-sans text-brand-gray-500 text-sm mt-1">
            {nuevasCount} nuevas {nuevasCount === 1 ? 'recomendación' : 'recomendaciones'} pendientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { key: 'todas' as const, label: 'Todas' },
            { key: 'nuevas' as const, label: 'Nuevas' },
            { key: 'leidas' as const, label: 'Leídas' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full font-sans text-sm transition-colors whitespace-nowrap ${
                filter === f.key
                  ? 'bg-brand-black text-white'
                  : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-brand-gray-500 text-sm py-20">
          <i className="ri-loader-4-line animate-spin" />
          <span>Cargando recomendaciones...</span>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((rec) => {
            const config = tipoConfig[rec.tipo] || tipoConfig.sugerencia;
            return (
              <div
                key={rec.id}
                className={`bg-white border ${
                  rec.leida ? 'border-brand-gray-200 opacity-70' : 'border-brand-gray-200'
                } rounded-2xl p-5 md:p-6`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}
                  >
                    <div className={`w-5 h-5 flex items-center justify-center ${config.text}`}>
                      <i className={`${config.icon} text-lg`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${config.bg} ${config.text}`}
                      >
                        {config.label}
                      </span>
                      {!rec.leida && (
                        <span className="w-2 h-2 rounded-full bg-brand-sage" />
                      )}
                      <span className="font-sans text-brand-gray-400 text-xs">
                        {new Date(rec.fecha).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    </div>
                    <h3 className="font-serif text-brand-black text-lg font-medium mt-2">
                      {rec.titulo}
                    </h3>
                    <p className="font-sans text-brand-gray-600 text-sm leading-relaxed mt-2">
                      {rec.descripcion}
                    </p>
                    {!rec.leida && (
                      <button
                        onClick={() => onMarkAsRead(rec.id)}
                        className="mt-4 inline-flex items-center gap-2 font-sans text-brand-black text-sm font-medium hover:text-brand-gray-600 transition-colors"
                      >
                        <i className="ri-check-line" />
                        Marcar como leída
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto rounded-full bg-brand-gray-100 flex items-center justify-center text-brand-gray-400 mb-4">
            <i className="ri-magic-line text-2xl" />
          </div>
          <p className="font-sans text-brand-gray-500 text-sm">
            No hay recomendaciones {filter === 'nuevas' ? 'nuevas' : filter === 'leidas' ? 'leídas' : ''}.
          </p>
        </div>
      )}
    </div>
  );
}